import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, ShoppingCart, User, Building2, Store, Crown, ChevronDown, ChevronRight, MapPin, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useNavCategories } from '@/context/NavCategoriesContext';
import { scheduleReleaseUiScrollLock } from '@/utils/releaseUiScrollLock';

export const Header = () => {
  const { categories: CATEGORIES } = useNavCategories();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const megaMenuRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const { isAuthenticated, user, logout, isAdmin, isStoreOwner } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    setMobileMenuOpen(false);
    setShowMegaMenu(false);
    logout();
    navigate('/');
    scheduleReleaseUiScrollLock();
  };

  useEffect(() => {
    setMobileMenuOpen(false);
    setShowMegaMenu(false);
    scheduleReleaseUiScrollLock();
  }, [location.pathname]);

  /** Inlogg/utlogg: stäng menyer utan att remounta Sheet (undviker kvarlämnad scroll-lås). */
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowMegaMenu(false);
    scheduleReleaseUiScrollLock();
  }, [isAuthenticated]);

  useEffect(() => {
    const onAuthLogout = () => {
      setMobileMenuOpen(false);
      setShowMegaMenu(false);
      scheduleReleaseUiScrollLock();
    };
    window.addEventListener('auth:logout', onAuthLogout);
    return () => window.removeEventListener('auth:logout', onAuthLogout);
  }, []);

  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) scheduleReleaseUiScrollLock();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  /** Öppen mobil-Sheet + resize till lg: innehållet portaleras och kan blockera klick även när hamburgaren döljs. */
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (!mq.matches) return;
      setMobileMenuOpen((wasOpen) => {
        if (wasOpen) scheduleReleaseUiScrollLock();
        return false;
      });
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Handle mouse enter - clear any pending close timeout and show menu
  const handleMenuEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setShowMegaMenu(true);
  };

  // Handle mouse leave - add delay before closing to allow diagonal mouse movement
  const handleMenuLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowMegaMenu(false);
      setActiveCategory(null);
    }, 150); // 150ms delay gives user time to move mouse to dropdown
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Close mega menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(event.target)) {
        setShowMegaMenu(false);
        setActiveCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header 
      className="sticky top-0 z-50 bg-white" 
      style={{ 
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05), 0 4px 12px -2px rgba(0,0,0,0.03)',
        borderBottom: '1px solid rgba(0,0,0,0.04)'
      }} 
      data-testid="header"
    >
      <div className="container-main">
        {/* Högre navigation - 72px höjd */}
        <div className="flex items-center justify-between h-[72px]">
          {/* Left: Hamburger + Logo */}
          <div className="flex items-center gap-4">
            <Sheet
              modal={false}
              open={mobileMenuOpen}
              onOpenChange={(open) => {
                setMobileMenuOpen(open);
                if (!open) scheduleReleaseUiScrollLock();
              }}
            >
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden hover:bg-slate-100" data-testid="menu-toggle">
                  <Menu className="h-6 w-6 text-[#0B1E33]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] p-0" hideOverlay>
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-slate-100">
                    <Link 
                      to="/" 
                      className="flex items-center" 
                      onClick={() => { setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    >
                      <span className="text-2xl font-heading font-black text-[#0B1E33]">Bygg</span>
                      <span className="text-2xl font-heading font-black text-[#F97316]">Fynd</span>
                    </Link>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto py-4">
                    {/* Mobile Categories */}
                    <div className="px-4 mb-4">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Kategorier</p>
                      {CATEGORIES.map((category) => (
                        <div key={category.id} className="mb-2">
                          <Link
                            to={`/produkter?category=${category.id}`}
                            className="block py-2 font-medium text-[#0F172A] hover:text-[#F97316] transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {category.name}
                          </Link>
                          {category.subcategories && (
                            <div className="pl-4 space-y-1">
                              {category.subcategories.map((sub) => (
                                <Link
                                  key={sub.slug}
                                  to={`/produkter?category=${category.id}&subcategory=${sub.slug}`}
                                  className="block py-1 text-sm text-gray-600 hover:text-[#F97316]"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Mobile Menu Links */}
                    <div className="border-t px-4 pt-4">
                      <Link
                        to="/butiker"
                        className="flex items-center gap-2 py-3 font-medium hover:text-[#F97316] transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Store className="h-4 w-4" /> Byggvaruhus
                      </Link>
                      <Link
                        to="/karta"
                        className="flex items-center gap-2 py-3 font-medium hover:text-[#F97316] transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <MapPin className="h-4 w-4" /> Karta
                      </Link>
                      {isStoreOwner && (
                        <Link
                          to="/butik-dashboard"
                          className="flex items-center gap-2 py-3 font-medium"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Store className="h-4 w-4" /> Min butik
                        </Link>
                      )}
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-2 py-3 font-medium"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Crown className="h-4 w-4" /> Admin
                        </Link>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Auth */}
                  <div className="border-t p-4">
                    {isAuthenticated ? (
                      <div className="space-y-2">
                        <Link to="/min-profil" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full flex items-center gap-2" data-testid="mobile-profile-link">
                            <UserCircle className="h-4 w-4" /> Min profil
                          </Button>
                        </Link>
                        <p className="text-sm text-gray-500 text-center">{user?.email}</p>
                        <Button variant="outline" className="w-full" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                          Logga ut
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Link to="/logga-in" onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full bg-[#003B73]">Logga in / Skapa konto</Button>
                        </Link>
                        <Link to="/bli-partner" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full">Bli partner</Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            
            {/* Logo - Robust */}
            <Link 
              to="/" 
              className="flex items-center gap-0.5" 
              data-testid="logo-link"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <span className="text-[26px] font-heading font-black text-[#0B1E33] tracking-tight">Bygg</span>
              <span className="text-[26px] font-heading font-black text-[#F97316] tracking-tight">Fynd</span>
            </Link>
          </div>
          
          {/* Center: Desktop Navigation - Premium spacing */}
          <nav className="hidden lg:flex items-center gap-1" ref={megaMenuRef}>
            {/* Categories Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleMenuEnter}
              onMouseLeave={handleMenuLeave}
            >
              <button 
                className="relative flex items-center gap-1.5 px-5 py-2.5 text-[14px] font-semibold text-[#0B1E33] hover:text-[#1E3A5F] transition-colors group"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Kategorier 
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showMegaMenu ? 'rotate-180' : ''}`} />
                <span className="absolute bottom-0 left-5 right-5 h-0.5 bg-[#F97316] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </button>
              
              {/* Mega Menu - Premium design with hover bridge */}
              {showMegaMenu && (
                <>
                  {/* Invisible hover bridge to prevent menu from closing */}
                  <div className="absolute top-full left-0 w-full h-4 bg-transparent" />
                  <div 
                    className="absolute top-full left-0 w-[640px] bg-white rounded-xl pt-4 overflow-hidden"
                    style={{ 
                      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15), 0 12px 24px -8px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(0,0,0,0.05)',
                      marginTop: '0px'
                    }}
                  >
                    {/* Inner content with padding to create visual gap */}
                    <div className="bg-white rounded-xl overflow-hidden" style={{ marginTop: '-4px' }}>
                      <div className="flex">
                        {/* Main Categories */}
                        <div className="w-1/2 p-5 border-r border-slate-100">
                          {CATEGORIES.map((category) => (
                            <Link
                              key={category.id}
                              to={`/produkter?category=${category.id}`}
                              className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-150 ${
                                activeCategory === category.id 
                                  ? 'bg-[#F97316]/10 text-[#F97316]' 
                                  : 'hover:bg-slate-50 text-[#0B1E33]'
                              }`}
                              onMouseEnter={() => setActiveCategory(category.id)}
                              style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '14px' }}
                            >
                              <span className="truncate">{category.name}</span>
                              {category.subcategories && <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0 ml-2" />}
                            </Link>
                          ))}
                        </div>
                        
                        {/* Subcategories */}
                        <div className="w-1/2 p-5 bg-slate-50/70">
                          {activeCategory ? (
                            <>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 px-3">
                                {CATEGORIES.find(c => c.id === activeCategory)?.name}
                              </p>
                              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                                {CATEGORIES.find(c => c.id === activeCategory)?.subcategories?.map((sub) => (
                                  <Link
                                    key={sub.slug}
                                    to={`/produkter?category=${activeCategory}&subcategory=${sub.slug}`}
                                    className="block px-3 py-2 text-sm text-gray-700 hover:text-[#F97316] hover:bg-white rounded-lg transition-colors"
                                    onClick={() => setShowMegaMenu(false)}
                                  >
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                              <Link
                                to={`/produkter?category=${activeCategory}`}
                                className="block mt-5 px-3 py-2 text-[13px] font-bold text-[#F97316] hover:text-[#EA580C] transition-colors"
                                onClick={() => setShowMegaMenu(false)}
                              >
                                Visa alla produkter →
                              </Link>
                            </>
                          ) : (
                            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                              Välj en kategori
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Premium nav links */}
            <Link 
              to="/butiker" 
              className="relative px-5 py-2.5 text-[14px] font-semibold text-[#0B1E33] hover:text-[#1E3A5F] transition-colors group"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Byggvaruhus
              <span className="absolute bottom-0 left-5 right-5 h-0.5 bg-[#F97316] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </Link>
            <Link 
              to="/karta" 
              className="relative px-5 py-2.5 text-[14px] font-semibold text-[#0B1E33] hover:text-[#1E3A5F] transition-colors group"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Hitta butik
              <span className="absolute bottom-0 left-5 right-5 h-0.5 bg-[#F97316] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </Link>
            
            {isStoreOwner && (
              <Link 
                to="/butik-dashboard" 
                className="relative px-5 py-2.5 text-[14px] font-semibold text-[#0B1E33] hover:text-[#1E3A5F] transition-colors group"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Min butik
                <span className="absolute bottom-0 left-5 right-5 h-0.5 bg-[#F97316] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </Link>
            )}
            {isAdmin && (
              <Link 
                to="/admin" 
                className="relative flex items-center gap-1.5 px-4 py-2 text-[13px] font-bold text-white rounded-full transition-all duration-200 hover:shadow-lg group"
                style={{ 
                  background: 'linear-gradient(135deg, #003B73 0%, #1E3A5F 100%)',
                  fontFamily: 'var(--font-heading)'
                }}
                data-testid="admin-nav-link"
              >
                <UserCircle className="h-4 w-4" /> Admin
              </Link>
            )}
          </nav>
          
          {/* Right: Auth + Cart */}
          <div className="flex items-center gap-4">
            {/* Desktop Auth */}
            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <Link to="/min-profil" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="desktop-profile-link">
                    <div className="w-8 h-8 rounded-full bg-[#003B73]/10 flex items-center justify-center" data-testid="user-avatar">
                      <UserCircle className="h-5 w-5 text-[#003B73]" />
                    </div>
                    <span className="text-[13px] text-slate-500 max-w-[120px] truncate">{user?.name || user?.email}</span>
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLogout} 
                    className="text-slate-600 hover:text-[#0B1E33] hover:bg-slate-100 text-[13px] font-semibold"
                  >
                    Logga ut
                  </Button>
                </div>
              ) : (
                <>
                  <Link to="/logga-in">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex items-center gap-2 text-slate-600 hover:text-[#0B1E33] hover:bg-slate-100 text-[13px] font-semibold"
                    >
                      <User className="h-4 w-4" /> Logga in
                    </Button>
                  </Link>
                  <Link to="/bli-partner">
                    <Button 
                      size="sm" 
                      className="flex items-center gap-2 text-white rounded-full px-6 py-2.5 text-[13px] font-bold transition-all duration-200 hover:shadow-lg"
                      style={{ 
                        background: 'linear-gradient(135deg, #0B1E33 0%, #1E3A5F 100%)',
                        fontFamily: 'var(--font-heading)'
                      }}
                    >
                      <Building2 className="h-4 w-4" /> Bli partner
                    </Button>
                  </Link>
                </>
              )}
            </div>
            
            {/* Cart - Premium */}
            <Link to="/varukorg" data-testid="cart-button">
              <Button 
                variant="ghost" 
                size="icon" 
                className="relative hover:bg-slate-100 rounded-full w-11 h-11 transition-colors"
              >
                <ShoppingCart className="h-5 w-5 text-[#0B1E33]" />
                {cartCount > 0 && (
                  <span 
                    className="absolute -top-0.5 -right-0.5 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-bold"
                    style={{ 
                      background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                      boxShadow: '0 2px 8px -2px rgba(249, 115, 22, 0.5)'
                    }}
                  >
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};
